import React, { useState } from 'react';
import { Box, Text, Grid, Icon, Input } from '@chakra-ui/react';
import { WeightSlider } from './WeightSlider';
import { UserFeedPreference, WeightsDto } from '../../../apis/user-preferences';
import Badge from '../../Badge';
import EditIcon from '../../../assets/edit-square-icon.svg';
import Psychology from '../../../assets/psychology.svg';
import ArrowLeft from '../../../assets/arrow-left-white-icon.svg';

interface AlgorithmSetting {
  id: keyof WeightsDto;
  name: string;
  description: string;
  color: string;
}

interface ConfigPresetProps {
  currentWeights: WeightsDto | null;
  handleWeightChange: (settingId: keyof WeightsDto, value: number) => void;
  handleBackToFeed1: () => void;
  algorithmSettings: AlgorithmSetting[];
  selectedPreset: UserFeedPreference | null;
  getPresetIcon: (preset: UserFeedPreference) => React.ReactElement;
  updatePresetName: (params: { presetId: string; name: string }) => void;
}

export const ConfigPreset: React.FC<ConfigPresetProps> = ({
  currentWeights,
  handleWeightChange,
  handleBackToFeed1,
  algorithmSettings,
  selectedPreset,
  getPresetIcon,
  updatePresetName
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');

  if (!selectedPreset) return null;

  const handleEditClick = () => {
    setNewName(selectedPreset.name);
    setIsEditing(true);
  };

  const handleNameSubmit = () => {
    if (newName.trim() && newName !== selectedPreset.name) {
      updatePresetName({ presetId: selectedPreset.id, name: newName.trim() });
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  return (
    <>
      <Box className="flex items-start gap-4 mb-4">
        <div
          className="p-1 md:p-2 bg-growth-green-500 rounded-full transition-transform duration-300 ease-in-out transform hover:scale-105 cursor-pointer flex-shrink-0 mt-1"
          onClick={handleBackToFeed1}
        >
          <img
            src={ArrowLeft}
            alt="Go Back"
            className="w-[17px] h-[17px] md:w-[20px] md:h-[20px]"
          />
        </div>
        <Box className="flex flex-col">
          <Text fontWeight="bold" className="flex flex-row items-center gap-1">
            {selectedPreset._isCustom ? 'Custom Mode Settings' : 'Mode Details'}
            <img src={Psychology} alt="" className="w-4 h-4" />
          </Text>
          <Text className="">
            {selectedPreset._isCustom
              ? 'View and Update your mode settings.'
              : 'View preset mode settings'}
          </Text>
        </Box>
      </Box>
      <Box>
        <Box mb={6} p={4} borderRadius="md" borderWidth={1} borderColor="#C8E6C9">
          <Text fontSize="sm" className="flex flex-row items-center gap-2">
            <Box bg="green.50" p={1.5} borderRadius="full">
              {getPresetIcon(selectedPreset)}
            </Box>
            {isEditing ? (
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={handleNameSubmit}
                onKeyDown={handleKeyPress}
                autoFocus
                size="sm"
                width="200px"
                height="10px"
              />
            ) : (
              <>
                <strong>{selectedPreset.name}</strong>
                {selectedPreset._isCustom && (
                  <img
                    src={EditIcon}
                    alt=""
                    className="w-4 h-4 cursor-pointer"
                    onClick={handleEditClick}
                  />
                )}
              </>
            )}
            {selectedPreset._isCustom ? (
              <Badge
                color="#2D211C"
                bgColor="#EDF7EE"
                fontSize="12px"
                fontWeight="600"
                text="Custom Mode"
              />
            ) : (
              <Badge
                color="#2D211C"
                bgColor="#F0EDEC"
                fontSize="12px"
                fontWeight="600"
                text="Preset Mode"
              />
            )}
          </Text>
          <Text fontSize="sm" className="mt-2">
            {selectedPreset.description}
          </Text>
        </Box>

        <Box mb={6} p={4} borderRadius="md" borderWidth={1} borderColor="#C8E6C9">
          <Text fontSize="sm">
            <strong>Advanced Settings:</strong>
            <br />
            <strong>Fine-tune your feed:</strong>
            <br />
            Tell the system what matters most to you by sliding each bar:
            <div className="mt-2"></div>
            <table>
              <tr>
                <td className="pr-3 align-top">
                  <strong>Center</strong>
                </td>
                <td>Default, no changes</td>
              </tr>
              <tr>
                <td className="pr-3 align-top">
                  <strong>Left</strong>
                </td>
                <td>Down-weight this factor (you'll see fewer articles that score high here)</td>
              </tr>
              <tr>
                <td className="pr-3 align-top">
                  <strong>Right</strong>
                </td>
                <td>Up-weight this factor (you'll see more articles that score high here)</td>
              </tr>
            </table>
            <div className="mt-2">
              <strong>For example:</strong> If the <strong>Educational Value</strong> us bumped
              above 100% it will surface more in-depth lessons, or if Recency is slided toward 50%
              the feed will focus on evergreen stories instead of the newest posts.
            </div>
          </Text>
        </Box>

        {currentWeights && (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={8}>
            {algorithmSettings.map((setting) => (
              <WeightSlider
                key={setting.id}
                id={setting.id}
                name={setting.name}
                description={setting.description}
                value={currentWeights[setting.id]}
                color={setting.color}
                onChange={(value) => handleWeightChange(setting.id, value)}
                disabled={!selectedPreset._isCustom}
              />
            ))}
          </Grid>
        )}
      </Box>
    </>
  );
};
